import type { ChatClient } from '@wtfalch/chat';
import { Button, Dialog, Divider, Empty, Icon, IdentityStack, Menu } from '@wtfalch/design';

type MenuItem = React.ComponentProps<typeof Menu>['items'][number];

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { parseCallNotice } from '../calls/callNotice.ts';
import { linkify } from '../mail/format.ts';
import {
  type ChatLocation,
  isPlainClick,
  navigate,
  parseRoomUrl,
  pathForRoute,
} from '../routes.ts';
import { PersonMessage, placeCard } from '../ui/people.tsx';
import { fileSize, guestUntilLabel, layoutTimeline, shortName } from './format.ts';
import { MessageEditor } from './MessageEditor.tsx';
import type { ChatTimelineEvent } from './types.ts';
import type { useTimeline } from './useTimeline.ts';

/** A short, fixed row rather than a whole emoji picker -- Matrix takes any
 *  string as a reaction key, so a picker is a thing that could grow later
 *  without anything already sent having to change. The same set
 *  `@wtfalch/chat`'s own (unused here) `MessageRow` offers. */
const IMAGE_FAILED_MESSAGE = 'Could not load this image.';
const DOWNLOAD_FAILED_MESSAGE = "Couldn't download this file. Try again.";

const QUICK_REACTIONS = ['👍', '🎉', '👀', '❤️', '😄', '🤔'];

/** The "+" a message's picker belongs to: its own toolbar's, or the one on
 *  its reactions row. */
function addButtonFor(eventId: string): HTMLElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLElement>('.chat-message-row'))
      .find((row) => row.dataset.eventId === eventId)
      ?.querySelector<HTMLElement>('[aria-label="Add a reaction"]') ?? null
  );
}

/** The quick reactions as a small popover at the "+": under it, or over it when
 *  the pane ends there. A portal, so opening it moves nothing in the stream. */
function ReactionPicker({
  anchor,
  onPick,
}: {
  anchor: HTMLElement;
  onPick: (key: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!box.current) return;
    setAt(
      placeCard(
        anchor.getBoundingClientRect(),
        { width: box.current.offsetWidth, height: box.current.offsetHeight },
        anchor.closest('[data-card-bounds]')?.getBoundingClientRect() ?? {
          top: 0,
          bottom: window.innerHeight,
        },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [anchor]);
  return createPortal(
    // biome-ignore lint/a11y/useSemanticElements: a row of buttons, not a form's fieldset.
    <div
      ref={box}
      role="group"
      aria-label="Quick reactions"
      className="pop flex items-center gap-1 p-1 chat-react-picker"
      style={{
        position: 'fixed',
        top: at?.top ?? 0,
        left: at?.left ?? 0,
        visibility: at ? 'visible' : 'hidden',
      }}
    >
      {QUICK_REACTIONS.map((key) => (
        <button
          key={key}
          type="button"
          className="chat-react-choice"
          aria-label={`React with ${key}`}
          onClick={() => onPick(key)}
        >
          {key}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/**
 * A room's or a thread's messages: day dividers, a "New" divider at the
 * read marker, and one row each -- `@wtfalch/design`'s `Message`, at
 * `density="tight"`, added expressly to match the canvas's `msg()` spacing
 * this file used to hand-roll (avatar, name and time on one line, the body
 * right under it at a 3px gap; design#141). Shared between `Room.tsx`'s
 * main stream and `ThreadPanel.tsx`'s replies, which is why scrolling and
 * read-tracking live in `useTimeline` rather than here.
 *
 * **What still sits beside `Message` rather than inside it.** Reply-in-thread
 * and pin have no slot on `Message` -- its own reactions row already draws
 * the add-reaction button `.chat-message-toolbar` used to hold, but nothing there
 * takes a second or third action. `MessageExtras` draws those two, and the
 * quick-reaction picker, its own hover-shown corner rather than widening
 * `Message`'s contract for two actions this section alone has. The thread
 * link keeps its own `IdentityStack` preview of who replied -- richer than
 * `Message`'s own `thread` slot (a count and a time, no faces) -- so it stays
 * `ThreadLink`, rendered after `Message` rather than through that prop.
 *
 * **No `formatted_body`.** `sanitizeHtml` belongs to `@wtfalch/chat`'s own
 * (unused here) React package, not to its public exports -- drawing a
 * stranger's HTML without it would be the exact hole it exists to close, so
 * every message draws its plain `body` instead, whatever richer text it
 * carried.
 *
 * **No thread's "last at" time.** The canvas's mock threads a reply line
 * with when the last one landed; `ChatThreadSummary` carries a reply count
 * and who replied, never a timestamp, so the link here says only those two
 * things rather than inventing a time nobody sent it at.
 *
 * **Never a raw Matrix id.** `senderName` and a thread's `participants`
 * both fall back to the bare id when nobody has set a display name --
 * `shortName` (`format.ts`) reads its localpart instead everywhere a name
 * is drawn here.
 */
export function Stream({
  client,
  roomId,
  threadId,
  timeline,
  onOpenThread,
  pinnedIds,
  onTogglePin,
  linkFor,
  emptyLabel,
}: {
  client: ChatClient;
  roomId: string;
  threadId: string | null;
  timeline: ReturnType<typeof useTimeline>;
  onOpenThread?: (rootId: string) => void;
  /** Which of this page's events are currently pinned (#255), and the way
   *  to flip one -- both optional, and both left out together: a thread's
   *  own stream (`ThreadPanel.tsx`) pins nothing, `m.room.pinned_events`
   *  being room-scoped, not thread-scoped, so its `Stream` passes neither
   *  and gets no pin button rather than one that would do nothing. */
  pinnedIds?: ReadonlySet<string>;
  onTogglePin?: (eventId: string) => void;
  /** The address of a place in this room, for the menu's "Copy link"; left
   *  out, the menu offers no link. A message has no address of its own, so a
   *  link names the room, or the thread the message belongs to. */
  linkFor?: (location: ChatLocation) => string;
  emptyLabel: string;
}) {
  const { readUpToId, scrollRef, onScroll } = timeline;
  // Call notices are signals for the ringing watcher, not conversation.
  const page = {
    ...timeline.page,
    events: timeline.page.events.filter((e) => parseCallNotice(e.body) === null),
  };
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // A picker belongs to the room it was opened in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the room and thread ids are the trigger, not values read here.
  useEffect(() => {
    setPickerFor(null);
    setEditingId(null);
    setDeletingId(null);
    setActionError(null);
  }, [roomId, threadId]);

  // Escape or a press outside closes the picker; Escape hands focus back to
  // the "+" it was opened from.
  useEffect(() => {
    if (!pickerFor) return;
    const addButton = () => addButtonFor(pickerFor);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setPickerFor(null);
      addButton()?.focus();
    };
    const onPress = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('.chat-react-picker') || addButton()?.contains(target)) return;
      setPickerFor(null);
    };
    const onScroll = () => setPickerFor(null);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPress);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPress);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pickerFor]);

  if (page.events.length === 0) {
    return (
      <div className="flex flex-1 flex-col min-h-0 overflow-y-auto py-2 px-5 stream">
        <Empty icon="chat">{emptyLabel}</Empty>
      </div>
    );
  }

  const rows = layoutTimeline(page.events, readUpToId, new Date());
  // Receipts are drawn once, under the newest message: a receipt on a
  // message covers everything above it, and one line per row is noise.
  const lastSentId = page.events.filter((e) => e.status === 'sent').at(-1)?.id;
  const seenBy = lastSentId ? client.readBy(roomId, lastSentId) : [];
  const guests = new Map(
    client.roomMembers(roomId).map((m) => [m.userId, guestUntilLabel(m.guest)]),
  );

  return (
    <div
      className="flex flex-1 flex-col min-h-0 overflow-y-auto py-2 px-5 stream"
      data-card-bounds
      ref={scrollRef}
      onScroll={onScroll}
    >
      {rows.map((row) => {
        if (row.kind === 'divider') {
          return <Divider key={row.key} label={row.label} tone="quiet" />;
        }
        if (row.kind === 'new') {
          return <Divider key={row.key} label="New" tone="accent" />;
        }
        const event = row.event;
        // `const` copies, not the props themselves: TypeScript's narrowing
        // from `Boolean(onOpenThread)`/`Boolean(onTogglePin)` does not
        // survive into the closures below, which capture the prop fresh
        // and so see it as possibly `undefined` again -- these, never
        // reassigned, do.
        const startThread = onOpenThread;
        const togglePin = onTogglePin;
        const canReact = event.status === 'sent' && !event.deleted;
        const canStartThread = canReact && !threadId && !event.thread && Boolean(startThread);
        const canPin = canReact && Boolean(togglePin);
        const isPinned = pinnedIds?.has(event.id) ?? false;
        // Reply-in-thread and pin have nowhere on `Message` to live, so the
        // corner toolbar draws them. Add-reaction is `Message`'s own once a
        // message has reactions; before that it sits here too, so a message
        // with none does not carry a bare "+" row.
        const hasReactions = event.reactions.length > 0;
        const toggleAddReaction = () => setPickerFor((cur) => (cur === event.id ? null : event.id));
        const canEdit = event.mine && canReact && !event.attachment && !event.upload;
        const canDelete = event.mine && canReact;
        const link =
          canReact && linkFor
            ? linkFor({
                roomId,
                threadId: threadId ?? (event.thread ? event.id : null),
                eventId: null,
              })
            : null;
        const menuItems: MenuItem[] = [
          ...(canEdit
            ? [
                {
                  id: 'edit',
                  label: 'Edit',
                  icon: 'pen' as const,
                  onAction: () => setEditingId(event.id),
                },
              ]
            : []),
          ...(canPin && togglePin
            ? [
                {
                  id: 'pin',
                  label: isPinned ? 'Unpin' : 'Pin',
                  icon: 'pin' as const,
                  onAction: () => togglePin(event.id),
                },
              ]
            : []),
          ...(link
            ? [
                {
                  id: 'link',
                  label: 'Copy link',
                  icon: 'copy' as const,
                  onAction: () => {
                    navigator.clipboard
                      .writeText(link)
                      .catch(() => setActionError("Couldn't copy the link."));
                  },
                },
              ]
            : []),
          ...(canDelete
            ? [
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: 'trash' as const,
                  danger: true,
                  separated: true,
                  onAction: () => setDeletingId(event.id),
                },
              ]
            : []),
        ];
        const showToolbar =
          (canStartThread && startThread) ||
          (canPin && togglePin) ||
          (canReact && !hasReactions) ||
          menuItems.length > 0;
        return (
          // The header/body/reactions triple is `@wtfalch/design`'s `Message`, at
          // `density="tight"` -- the canvas's own `msg()` numbers (design#141), so
          // this row no longer states them. `relative` is `.chat-message-toolbar`'s
          // anchor, and this `py-1.5` (6px) plus `Message`'s own 4px (`py-1`) is
          // `msg()`'s 10px.
          <div
            key={row.key}
            className={[
              'relative py-1.5 px-0 chat-message-row',
              event.status === 'failed' && 'is-failed',
              row.collapsed && 'is-continued',
            ]
              .filter(Boolean)
              .join(' ')}
            data-event-id={event.id}
          >
            <PersonMessage
              chatUserId={event.senderId}
              name={event.mine ? 'You' : shortName(event.senderName)}
              {...(event.mine ? { discName: shortName(event.senderName) } : {})}
              address={event.senderId}
              {...(guests.get(event.senderId) ? { guestUntil: guests.get(event.senderId) } : {})}
              at={new Date(event.ts)}
              collapsed={row.collapsed}
              density="tight"
              body={
                editingId === event.id ? (
                  <MessageEditor
                    initial={event.body}
                    onCancel={() => setEditingId(null)}
                    onSave={async (text) => {
                      await client.editMessage(roomId, event.id, text);
                      setEditingId(null);
                    }}
                  />
                ) : (
                  <MessageBody client={client} roomId={roomId} event={event} />
                )
              }
              {...(hasReactions
                ? {
                    reactions: event.reactions.map((r) => ({
                      glyph: r.key,
                      count: r.count,
                      mine: r.mine,
                    })),
                  }
                : {})}
              onToggleReaction={
                canReact
                  ? (glyph) => void client.toggleReaction(roomId, event.id, glyph)
                  : undefined
              }
              onAddReaction={canReact ? toggleAddReaction : undefined}
            />
            {pickerFor === event.id && addButtonFor(event.id) && (
              <ReactionPicker
                anchor={addButtonFor(event.id) as HTMLElement}
                onPick={(key) => {
                  void client.toggleReaction(roomId, event.id, key);
                  setPickerFor(null);
                }}
              />
            )}
            {!threadId && startThread && event.thread && (
              <div className="chat-message-extra">
                <ThreadLink thread={event.thread} onOpen={() => startThread(event.id)} />
              </div>
            )}
            {event.id === lastSentId && seenBy.length > 0 && (
              <p className="text-xs text-muted chat-message-extra chat-message-seen m-0">
                {seenLabel(seenBy.map((p) => shortName(p.name)))}
              </p>
            )}
            {event.status === 'pending' && (
              <p className="text-xs text-muted chat-message-extra chat-message-status m-0">
                {event.upload ? 'Uploading…' : 'Sending…'}
              </p>
            )}
            {event.status === 'failed' && (
              <div className="flex flex-col items-start gap-2 chat-message-extra chat-message-status is-failed">
                <p className="flex items-center gap-1 text-xs font-strong m-0">
                  <Icon name="warning" size={14} /> Not sent
                </p>
                <div className="flex items-center gap-2">
                  <Button size="sm" onPress={() => client.retryMessage(roomId, event.id)}>
                    <Icon name="refresh" size={14} /> Try again
                  </Button>
                  <Button
                    size="sm"
                    kind="ghost"
                    onPress={() => client.cancelMessage(roomId, event.id)}
                  >
                    Throw it away
                  </Button>
                </div>
                <p className="text-xs text-muted m-0">It is still here. Nothing was half-sent.</p>
              </div>
            )}
            {showToolbar && (
              <div className="flex items-center gap-3 chat-message-toolbar">
                {canReact && !hasReactions && (
                  <Button
                    kind="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Add a reaction"
                    onPress={toggleAddReaction}
                  >
                    <Icon name="plus" size={14} />
                  </Button>
                )}
                {canStartThread && startThread && (
                  <Button
                    kind="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Reply in thread"
                    onPress={() => startThread(event.id)}
                  >
                    <Icon name="reply" size={14} />
                  </Button>
                )}
                {canPin && togglePin && (
                  <Button
                    kind="ghost"
                    size="sm"
                    iconOnly
                    aria-label={isPinned ? 'Unpin this message' : 'Pin this message'}
                    aria-pressed={isPinned}
                    onPress={() => togglePin(event.id)}
                  >
                    <Icon name="pin" size={14} />
                  </Button>
                )}
                {menuItems.length > 0 && (
                  <Menu
                    label="Message actions"
                    placement="bottom end"
                    trigger={
                      <Button kind="ghost" size="sm" iconOnly aria-label="More actions">
                        <Icon name="more" size={14} />
                      </Button>
                    }
                    items={menuItems}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
      {actionError && (
        <p className="text-xs text-bad chat-message-extra m-0" role="alert">
          {actionError}
        </p>
      )}
      {deletingId && (
        <Dialog
          tone="bad"
          title="Delete this message?"
          onCancel={() => setDeletingId(null)}
          actions={
            <>
              <Button kind="ghost" onPress={() => setDeletingId(null)}>
                Keep it
              </Button>
              <Button
                onPress={() => {
                  const id = deletingId;
                  setDeletingId(null);
                  setActionError(null);
                  client.deleteMessage(roomId, id).catch(() => {
                    setActionError("Couldn't delete the message. Try again.");
                  });
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          It is removed for everyone in this room.
        </Dialog>
      )}
    </div>
  );
}

/** "Seen by Ana", "Seen by Ana and Bo", "Seen by Ana, Bo and 2 others". */
export function seenLabel(names: string[]): string {
  if (names.length === 1) return `Seen by ${names[0]}`;
  if (names.length === 2) return `Seen by ${names[0]} and ${names[1]}`;
  const rest = names.length - 2;
  return `Seen by ${names[0]}, ${names[1]} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
}

function ThreadLink({
  thread,
  onOpen,
}: {
  thread: NonNullable<ChatTimelineEvent['thread']>;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="flex items-center gap-3 chat-thread-link" onClick={onOpen}>
      <IdentityStack
        people={thread.participants.slice(0, 3).map((p) => ({ name: shortName(p), address: p }))}
        size="sm"
      />
      <span className="text-sm lnk">
        {thread.replyCount} {thread.replyCount === 1 ? 'reply' : 'replies'}
      </span>
    </button>
  );
}

function MessageBody({
  client,
  roomId,
  event,
}: {
  client: ChatClient;
  roomId: string;
  event: ChatTimelineEvent;
}) {
  if (event.deleted)
    return (
      <p className="text-sm text-muted chat-message-body m-0 whitespace-pre-wrap">
        Message deleted
      </p>
    );
  if (event.attachment)
    return (
      <AttachmentBody
        client={client}
        roomId={roomId}
        attachment={event.attachment}
        upload={event.upload}
      />
    );
  // A blank line starts a paragraph, spaced at half a line in `chat.css`; a
  // single line break stays a line break.
  const paragraphs = event.body.split(/\n{2,}/);
  const lastParagraph = paragraphs.length - 1;
  return (
    <div className="text-sm chat-message-body">
      {paragraphs.map((paragraph, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed run across one message body, never reordered.
        <p key={i} className="m-0 whitespace-pre-wrap">
          <Linkified text={paragraph} />
          {event.edited && i === lastParagraph && (
            <span className="text-xs text-muted chat-message-edited"> (edited)</span>
          )}
        </p>
      ))}
    </div>
  );
}

/**
 * A message body with its own Agora room link (`routes.ts`'s own
 * `parseRoomUrl`, agora#222) drawn to open in place, the same client-side
 * navigation `go` performs everywhere else in the app. Every other run of
 * text -- including any other `http(s)://` URL -- renders exactly as
 * `event.body` always has: this reuses `mail/format.ts`'s own `linkify` only
 * to find a room link's own run, not to turn every URL in a chat message into
 * a clickable one, which nothing here asked for.
 */
function Linkified({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((segment, i) => {
        if (!('url' in segment)) {
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered run across one message body, never reordered.
          return <Fragment key={i}>{segment.text}</Fragment>;
        }
        const room = parseRoomUrl(segment.url, window.location.origin);
        if (!room) {
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          return <Fragment key={i}>{segment.url}</Fragment>;
        }
        return (
          <a
            // biome-ignore lint/suspicious/noArrayIndexKey: see above
            key={i}
            href={segment.url}
            onClick={(event) => {
              if (!isPlainClick(event)) return;
              event.preventDefault();
              navigate(pathForRoute(room));
            }}
          >
            {segment.url}
          </a>
        );
      })}
    </>
  );
}

function AttachmentBody({
  client,
  roomId,
  attachment,
  upload,
}: {
  client: ChatClient;
  roomId: string;
  attachment: NonNullable<ChatTimelineEvent['attachment']>;
  upload: ChatTimelineEvent['upload'];
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `retry` only re-runs the load after a failure ("Retry").
  useEffect(() => {
    if (!attachment.isImage) return;
    let objectUrl: string | null = null;
    let live = true;
    setFailed(null);
    client.loadMedia(attachment.mxcUri, roomId, attachment.eventId).then(
      (u) => {
        if (!live) return;
        // `loadMedia` resolves `null` rather than rejecting on most failures.
        if (!u) setFailed(IMAGE_FAILED_MESSAGE);
        objectUrl = u;
        setUrl(u);
      },
      () => {
        if (live) setFailed(IMAGE_FAILED_MESSAGE);
      },
    );
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, roomId, attachment, retry]);

  if (upload) {
    const percent = upload.total > 0 ? Math.round((upload.loaded / upload.total) * 100) : 0;
    return (
      <p className="text-xs text-muted chat-message-status m-0">
        Uploading {attachment.name}… {percent}%
      </p>
    );
  }

  if (attachment.isImage) {
    if (url) return <img src={url} alt={attachment.name} className="chat-attachment-img" />;
    if (failed) {
      return (
        <p className="flex items-center gap-2 text-xs text-bad m-0" role="alert">
          {failed}
          <Button size="sm" onPress={() => setRetry((n) => n + 1)}>
            Retry
          </Button>
        </p>
      );
    }
    return <p className="text-xs text-muted">Loading image…</p>;
  }

  const size = fileSize(attachment.size);
  return (
    <>
      <button
        type="button"
        className="flex items-center gap-3 w-full max-w-sm p-3 text-left rounded-md border border-border surface-panel-2 chat-file-card"
        onClick={() =>
          void client
            .loadMedia(attachment.mxcUri, roomId, attachment.eventId)
            .then((u) => {
              if (!u) {
                setFailed(DOWNLOAD_FAILED_MESSAGE);
                return;
              }
              setFailed(null);
              const anchor = document.createElement('a');
              anchor.href = u;
              anchor.download = attachment.name;
              anchor.click();
              // A revoke on the same tick can cancel the download in Safari
              // and Firefox.
              setTimeout(() => URL.revokeObjectURL(u), 60_000);
            })
            .catch(() => setFailed(DOWNLOAD_FAILED_MESSAGE))
        }
      >
        <Icon name="file" size={20} />
        <span className="flex flex-col grow min-w-0">
          <span className="text-sm font-strong truncate">{attachment.name}</span>
          {size && <span className="text-xs text-muted">{size}</span>}
        </span>
        <Icon name="download" size={16} />
      </button>
      {failed && (
        <p className="text-xs text-bad m-0" role="alert">
          {failed}
        </p>
      )}
    </>
  );
}
